import { useApi } from '@/common/ApiHelper';
import type { AssessmentHeatmapEntry } from '@/components/analysis/types';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ConditionOperator, LogicalOperator } from '@sysarb/sysarb-api/models/common/selection/Selection';
import type { Guid } from 'guid-typescript';
import type { QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';

type SalaryReviewAssessmentsOptions = {
    queries: {
        /** Salary-criteria heatmap entries for the current manager's node, scoped to a salary review. */
        getSalaryReviewAssessments: (salaryReviewId: Guid | string | null) => QueryOptions<AssessmentHeatmapEntry[], Error>;
    };
};

export const salaryReviewAssessments: SalaryReviewAssessmentsOptions = {
    queries: {
        getSalaryReviewAssessments: (salaryReviewId: Guid | string | null): QueryOptions<AssessmentHeatmapEntry[], Error> => {
            const { selectedInstance, currentOrganisationNodeId } = useLoginStore();
            const managerIdValue = selectedInstance?.user?.Manager_ID ?? null;
            const nodeId = currentOrganisationNodeId;

            return queryOptions<AssessmentHeatmapEntry[], Error>({
                queryKey: ['manager', 'salary-review-assessments', nodeId, managerIdValue, salaryReviewId?.toString() ?? null],
                queryFn: async (): Promise<AssessmentHeatmapEntry[]> => {
                    const result = await useApi().salaryReviewAssessment.getBySelection({
                        Condition: {
                            LogicalOperator: LogicalOperator.And,
                            Conditions: [
                                {
                                    LogicalOperator: LogicalOperator.Or,
                                    Conditions: [
                                        {
                                            PropertyName: 'Manager_ID',
                                            ConditionOperator: ConditionOperator.EqualTo,
                                            Value: managerIdValue,
                                        },
                                        {
                                            PropertyName: 'ManagerDeputy',
                                            ConditionOperator: ConditionOperator.Contains,
                                            Value: managerIdValue,
                                        },
                                    ],
                                },
                                {
                                    PropertyName: 'OrganisationNode_ID',
                                    ConditionOperator: ConditionOperator.EqualTo,
                                    Value: nodeId,
                                },
                                {
                                    PropertyName: 'SalaryReview_ID',
                                    ConditionOperator: ConditionOperator.EqualTo,
                                    Value: salaryReviewId,
                                },
                            ],
                        },
                    });

                    const entries: AssessmentHeatmapEntry[] = [];
                    for (const assessment of result) {
                        const assessmentValue = assessment.assessmentValue;
                        if (assessmentValue?.value == null) continue;
                        entries.push({
                            criteriaId: assessment.salaryReviewSalaryCriteria.id.toString(),
                            criteriaName: assessment.salaryReviewSalaryCriteria.name,
                            assessmentValue: assessmentValue.value,
                            assessmentTitle: assessmentValue.title,
                            organisationNodeId: assessment.organisationNode.id.toString(),
                            salaryReviewEmploymentId: assessment.salaryReviewEmployment.id.toString(),
                        });
                    }
                    return entries;
                },
                enabled: () => salaryReviewId != null && nodeId != null && managerIdValue != null,
            });
        },
    },
};
